<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentBotFunctionsAPI from 'dashboard/api/agentBotFunctions';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';

const props = defineProps({
  agentBot: { type: Object, required: true },
});

const { t } = useI18n();
const functions = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showForm = ref(false);
const editingId = ref(null);
const form = ref({
  name: '',
  slug: '',
  description: '',
  outgoing_url: '',
  active: true,
});

const hasFunctions = computed(() => functions.value.length > 0);

const resetForm = () => {
  form.value = {
    name: '',
    slug: '',
    description: '',
    outgoing_url: '',
    active: true,
  };
  editingId.value = null;
  showForm.value = false;
};

const fetchFunctions = async () => {
  isLoading.value = true;
  try {
    const { data } = await AgentBotFunctionsAPI.getFunctions(props.agentBot.id);
    functions.value = data;
  } catch (_e) {
    functions.value = [];
  } finally {
    isLoading.value = false;
  }
};

const editFunction = fn => {
  editingId.value = fn.id;
  form.value = { ...fn };
  showForm.value = true;
};

const sanitizeSlug = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const saveFunction = async () => {
  isSaving.value = true;
  try {
    const payload = {
      ...form.value,
      slug: sanitizeSlug(form.value.slug || form.value.name),
    };
    if (editingId.value) {
      await AgentBotFunctionsAPI.updateFunction(
        props.agentBot.id,
        editingId.value,
        payload
      );
    } else {
      await AgentBotFunctionsAPI.createFunction(props.agentBot.id, payload);
    }
    useAlert(t('AGENT_BOTS.FUNCTIONS.SAVE_SUCCESS'));
    resetForm();
    await fetchFunctions();
  } catch (_e) {
    useAlert(t('AGENT_BOTS.FUNCTIONS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteFunction = async fn => {
  try {
    await AgentBotFunctionsAPI.deleteFunction(props.agentBot.id, fn.id);
    useAlert(t('AGENT_BOTS.FUNCTIONS.DELETE_SUCCESS'));
    await fetchFunctions();
  } catch (_e) {
    useAlert(t('AGENT_BOTS.FUNCTIONS.DELETE_ERROR'));
  }
};

onMounted(fetchFunctions);
</script>

<template>
  <div class="flex flex-col gap-4 mt-4 border-t border-n-weak pt-4">
    <div class="flex items-center justify-between">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.FUNCTIONS.TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ $t('AGENT_BOTS.FUNCTIONS.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        v-if="!showForm"
        xs
        type="button"
        :label="$t('AGENT_BOTS.FUNCTIONS.ADD')"
        icon="i-lucide-plus"
        @click.stop.prevent="showForm = true"
      />
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-11">
      {{ $t('AGENT_BOTS.FUNCTIONS.LOADING') }}
    </div>

    <ul v-else-if="hasFunctions" class="flex flex-col gap-2">
      <li
        v-for="fn in functions"
        :key="fn.id"
        class="flex items-start justify-between gap-3 p-3 rounded-lg border border-n-weak"
      >
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">{{ fn.name }}</p>
          <p v-if="fn.description" class="text-xs text-n-slate-11 truncate">
            {{ fn.description }}
          </p>
          <p class="text-xs text-n-slate-10 truncate mt-1">
            {{ fn.outgoing_url }}
          </p>
        </div>
        <div class="flex gap-1 flex-shrink-0">
          <NextButton
            xs
            slate
            type="button"
            :label="$t('AGENT_BOTS.FUNCTIONS.EDIT')"
            @click.stop.prevent="editFunction(fn)"
          />
          <NextButton
            xs
            ruby
            type="button"
            :label="$t('AGENT_BOTS.FUNCTIONS.DELETE')"
            @click.stop.prevent="deleteFunction(fn)"
          />
        </div>
      </li>
    </ul>

    <p v-else-if="!showForm" class="text-xs text-n-slate-11">
      {{ $t('AGENT_BOTS.FUNCTIONS.EMPTY') }}
    </p>

    <form
      v-if="showForm"
      class="flex flex-col gap-3 p-3 rounded-lg bg-n-alpha-1"
      @submit.prevent="saveFunction"
    >
      <Input
        v-model="form.name"
        :label="$t('AGENT_BOTS.FUNCTIONS.FORM.NAME')"
      />
      <Input
        v-model="form.slug"
        :label="$t('AGENT_BOTS.FUNCTIONS.FORM.SLUG')"
      />
      <TextArea
        v-model="form.description"
        :label="$t('AGENT_BOTS.FUNCTIONS.FORM.DESCRIPTION')"
      />
      <Input
        v-model="form.outgoing_url"
        :label="$t('AGENT_BOTS.FUNCTIONS.FORM.URL')"
      />
      <Switch v-model="form.active" />
      <div class="flex gap-2 justify-end">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="resetForm"
        />
        <NextButton
          type="submit"
          :is-loading="isSaving"
          :label="$t('AGENT_BOTS.FUNCTIONS.SAVE')"
        />
      </div>
    </form>
  </div>
</template>
